import { useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, LiquidityAccount } from "@kundeninfo/api-client";
import { Modal, ModalFuss, darfAbbrechen, useBeforeUnload, useBestaetigung, useFormularZustand } from "@kundeninfo/ui";
import { Eingabe, Formular, Hinweis, Schalter } from "@kundeninfo/ui/me-design";
import { errorProp, fieldErrorInsteadOf409 } from "../../components/fieldErrors.js";
import { formatDatum } from "../../format.js";
import { AMOUNT_FIELD_MAX, amountForApi, cents } from "../suppliers/shared.js";
import { euro } from "./liquidityModel.js";
import { LIQUIDITY_TEXT_WEB as T } from "./liquidityTexts.js";
import "./Liquidity.css";

/** Die Grenzen des Schemas (`ACCOUNT_NAME_MAX`, `NOTE_MAX` der Domäne, Rohwert der IBAN). web importiert die Domäne nicht. */
const ACCOUNT_NAME_MAX = 100, IBAN_MAX = 64, NOTE_MAX = 200;

const NAME_TAKEN: readonly string[] = ["ACCOUNT_NAME_TAKEN"];

type AccountDraft = { name: string; iban: string; active: boolean };
const accountDraft = (a: LiquidityAccount | null): AccountDraft => ({ name: a?.name ?? "", iban: a?.iban ?? "", active: a?.active ?? true });

/**
 * KONTO ANLEGEN ODER ÄNDERN (Spec „Dialoge“): Modalpanel rechts, „Speichern“ ist die Bestätigung, die Währung steht nur da (EUR). Ein
 * vergebener Name (409 `ACCOUNT_NAME_TAKEN`) ist ein Befund am Feld, kein fremder Stand; ein fremder Stand (409 Version) zeigt „Neu laden“.
 */
export function AccountPanel(p: {
  client: Pick<ApiClient, "createLiquidityAccount" | "updateLiquidityAccount" | "getLiquidityAccounts">;
  account: LiquidityAccount | null; companyId: string | null;
  /** Der Name des Unternehmens, das ein neues Konto bekommt, wenn die Sicht mehrere zeigt; sonst `null`. */
  companyName: string | null;
  onSaved: () => void; onCancel: () => void;
}): ReactElement {
  const [current, setCurrent] = useState(p.account);
  const [start, setStart] = useState(() => accountDraft(p.account));
  const [draft, setDraft] = useState(start);
  const [tried, setTried] = useState(false);
  const name = draft.name.trim(), iban = draft.iban.trim();
  const form = useFormularZustand<LiquidityAccount>({
    speichern: () => fieldErrorInsteadOf409(() => (current === null
      ? p.client.createLiquidityAccount({ ...(p.companyId === null ? {} : { companyId: p.companyId }), name, iban: iban === "" ? null : iban, active: draft.active })
      : p.client.updateLiquidityAccount(current.id, { version: current.version, name, iban: iban === "" ? null : iban, active: draft.active,
        sortIndex: current.sortIndex })), NAME_TAKEN),
    neuLaden: async () => {
      if (current === null) return;
      const { items } = await p.client.getLiquidityAccounts({ companyIds: [current.companyId] });
      const fresh = items.find((a) => a.id === current.id) ?? current;
      const d = accountDraft(fresh);
      setCurrent(fresh);
      setStart(d);
      setDraft(d);
    },
    bekannteFelder: ["name", "iban"],
  });
  const dirty = JSON.stringify(draft) !== JSON.stringify(start);
  useBeforeUnload(dirty);
  const nameError = form.feldFehler.name ?? (tried && name === "" ? T.dialogs.nameRequired : undefined);

  async function save(): Promise<void> {
    setTried(true);
    if (name === "") return;
    if ((await form.aktion()) !== undefined) p.onSaved();
  }
  async function leave(): Promise<void> {
    if (!form.laeuft && await darfAbbrechen(dirty)) p.onCancel();
  }

  return (
    <Modal titel={current === null ? T.dialogs.account.titleNew : T.dialogs.account.titleEdit} onAbbrechen={() => { void leave(); }} abbrechenGesperrt={form.laeuft}>
      {form.meldung !== null && <Hinweis art="fehler">{form.meldung}</Hinweis>}
      <Formular beschriftung="oben" {...form.formularProps}
        hinweis={form.zustand === "fremder-stand" ? <span>{T.dialogs.changedElsewhere}</span> : undefined}>
        <div className="liq__mask">
          {current === null && p.companyName !== null && <Eingabe beschriftung={T.dialogs.account.company} wert={p.companyName} gesperrt />}
          <Eingabe beschriftung={T.dialogs.account.name} wert={draft.name} pflicht maxLength={ACCOUNT_NAME_MAX} {...errorProp(nameError)}
            onGeaendert={(e) => setDraft((d) => ({ ...d, name: e.wert }))} />
          <Eingabe beschriftung={T.dialogs.account.iban} wert={draft.iban} maxLength={IBAN_MAX} {...errorProp(form.feldFehler.iban)}
            onGeaendert={(e) => setDraft((d) => ({ ...d, iban: e.wert }))} />
          <Eingabe beschriftung={T.dialogs.account.currency} wert={current?.currency ?? T.dialogs.account.currencyDefault} gesperrt />
          <Schalter beschriftung={T.dialogs.account.active} an={draft.active} onGeaendert={(e) => setDraft((d) => ({ ...d, active: e.an }))} />
        </div>
      </Formular>
      <ModalFuss speichernText={T.dialogs.save} speichernGesperrt={form.zustand !== "bereit"} abbrechenGesperrt={form.laeuft}
        onSpeichern={() => { void save(); }} onAbbrechen={() => { void leave(); }} />
    </Modal>
  );
}

/**
 * KONTOSTAND ERFASSEN (Spec „Dialoge“): „Stand am“ mit Vorgabe heute, Betrag rechtsbündig mit „€“ (negativ erlaubt), Bemerkung. „Speichern“
 * ist die Bestätigung; nur ein Stand am selben Tag fragt gestapelt, denn er ersetzt den vorhandenen.
 */
export function BalancePanel(p: {
  client: Pick<ApiClient, "addAccountBalance">; account: LiquidityAccount; today: string; onSaved: () => void; onCancel: () => void;
}): ReactElement {
  const start = { date: p.today, amount: "", note: "" };
  const [draft, setDraft] = useState(start);
  const [tried, setTried] = useState(false);
  const { bestaetigen } = useBestaetigung();
  const amount = amountForApi(draft.amount, { signed: true }), note = draft.note.trim();
  const form = useFormularZustand<LiquidityAccount>({
    speichern: () => p.client.addAccountBalance(p.account.id, { balanceDate: draft.date, amount: amount!, note: note === "" ? null : note }),
    neuLaden: async () => { /* Ein Stand trägt keine Version; ein fremder Stand kommt hier nicht vor. */ },
    bekannteFelder: ["balanceDate", "amount", "note"],
  });
  const dirty = draft.amount !== "" || draft.note !== "" || draft.date !== p.today;
  useBeforeUnload(dirty);

  async function save(): Promise<void> {
    setTried(true);
    if (amount === null || draft.date === "") return;
    const same = p.account.balances.find((b) => b.balanceDate === draft.date);
    if (same !== undefined && !(await bestaetigen({ titel: T.dialogs.balance.replaceTitle, bestaetigenText: T.dialogs.balance.replaceConfirm,
      text: T.dialogs.balance.replaces(euro(Number(cents(same.amount))), formatDatum(same.balanceDate)) }))) return;
    if ((await form.aktion()) !== undefined) p.onSaved();
  }
  async function leave(): Promise<void> {
    if (!form.laeuft && await darfAbbrechen(dirty)) p.onCancel();
  }

  return (
    <Modal titel={T.dialogs.balance.title} onAbbrechen={() => { void leave(); }} abbrechenGesperrt={form.laeuft}>
      {form.meldung !== null && <Hinweis art="fehler">{form.meldung}</Hinweis>}
      <Formular beschriftung="oben" {...form.formularProps}>
        <div className="liq__mask">
          <p className="liq__muted">{p.account.name}</p>
          <Eingabe art="datum" beschriftung={T.dialogs.balance.date} wert={draft.date} pflicht
            {...errorProp(form.feldFehler.balanceDate ?? (tried && draft.date === "" ? T.dialogs.dateRequired : undefined))}
            onGeaendert={(e) => setDraft((d) => ({ ...d, date: e.wert }))} />
          <Eingabe art="dezimal" beschriftung={T.dialogs.balance.amount} einheit={T.dialogs.euroUnit} wert={draft.amount} pflicht maxLength={AMOUNT_FIELD_MAX}
            {...errorProp(form.feldFehler.amount ?? (tried && amount === null ? T.dialogs.amountInvalid : undefined))}
            onGeaendert={(e) => setDraft((d) => ({ ...d, amount: e.wert }))} />
          <Eingabe beschriftung={T.dialogs.balance.note} mehrzeilig wert={draft.note} maxLength={NOTE_MAX} {...errorProp(form.feldFehler.note)}
            onGeaendert={(e) => setDraft((d) => ({ ...d, note: e.wert }))} />
        </div>
      </Formular>
      <ModalFuss speichernText={T.dialogs.save} speichernGesperrt={form.zustand !== "bereit"} abbrechenGesperrt={form.laeuft}
        onSpeichern={() => { void save(); }} onAbbrechen={() => { void leave(); }} />
    </Modal>
  );
}
